<template>
  <div class="c-app-update">
    <!-- "update available" prompt -->
    <v-snackbar
      v-show="isOrgLoaded"
      class="c-snack"
      content-class="c-snack__content"
      app
      centered
      color="primary"
      light
      multi-line
      :timeout="-1"
      :value="activationPrompt"
      vertical
    >
      <h5>{{ $t('app.update.title') }}</h5>
      <p class="mb-0 text-body-1">{{ $t('app.update.message') }}</p>
      <template #action="{ attrs }">
        <v-btn
          class="ma-auto"
          color="info"
          rounded
          small
          v-bind="attrs"
          @click="activateUpdate"
        >
          {{ $t('app.update.label') }}
          <v-icon> mdi-cellphone-arrow-down </v-icon>
        </v-btn>
      </template>
    </v-snackbar>

    <!-- "refresh coming" notification -->
    <v-snackbar
      v-show="isOrgLoaded"
      class="c-snack"
      content-class="c-snack__content"
      app
      centered
      color="primary"
      light
      multi-line
      :timeout="10000"
      :value="reloadNotification"
      vertical
    >
      <h5>{{ $t('app.reload.title') }}</h5>
      <p class="mb-0 text-body-1">
        {{ $t('app.refresh.message') }}
      </p>
    </v-snackbar>

    <!-- "reload required" prompt -->
    <ModalDialog
      v-if="isOrgLoaded"
      v-model="reloadPrompt"
      bordered
      confirm
      popup
      :title="$t('app.refresh.title')"
      :submit-text="$t('app.refresh.label')"
      @submit="reloadApp"
    >
      {{ $t('app.refresh.message') }}
    </ModalDialog>
  </div>
</template>

<script>
import { mapActions } from 'vuex'
import { APP_VERSION } from '@/config/appConfig.js'
import ModalDialog from '@/components/base/ModalDialog'

export default {
  name: 'AppUpdate',

  components: {
    ModalDialog
  },

  data() {
    return {
      isActivating: false,
      isReloading: false,
      serviceWorker: null,
      activationPrompt: false,
      reloadNotification: false,
      reloadPrompt: false
    }
  },

  computed: {
    isDark() {
      return this.$store.state.themeStore.isDark
    },

    isOrgLoaded() {
      return this.$store.state.orgStore.loading === false
    }
  },

  mounted: function () {
    // from service worker
    const once = true
    document.addEventListener('CustomUpdateAvailable', this.onUpdateAvailable, { once })
    document.addEventListener('CustomUpdatePending', this.onUpdated, { once })

    // from serice worker container
    document.addEventListener('CustomControllerChange', this.onControllerChange)
  },

  beforeDestroy: function () {
    const once = true
    document.removeEventListener('CustomUpdateAvailable', this.onUpdateAvailable, { once })
    document.removeEventListener('CustomUpdatePending', this.onUpdated, { once })
    document.removeEventListener('CustomControllerChange', this.onControllerChange)
  },

  methods: {
    ...mapActions('appStore', ['updateVersion']),

    // update detected (installed, but not activated)
    onUpdateAvailable(_e) {
      console.debug('[AppUpdate]: Service worker update available!')
      this.activationPrompt = true
    },

    // ...activate the installed updates
    activateUpdate(e) {
      // app instructs the pwa
      // (because the serviceWorker asked and the user agreed)
      this.activationPrompt = false

      // tell service worker to activate the update
      if (this.serviceWorker) {
        console.debug('[AppUpdate]: Tell service worker to activate.', e.detail)
        this.serviceWorker.postMessage({ type: 'SKIP_WAITING' })
        this.isActivating = true
      } else {
        console.error('[AppUpdate]: Unable to tell service worker to activate.', e.detail)
      }
    },

    // update activated (but needs a refresh)
    onUpdated(_e) {
      console.debug('[AppUpdate]: Service worker updated!')
      this.reloadNotification = true
    },

    // an updated service worker is available
    onControllerChange(e) {
      // called regardless of whether the app was informed of the update
      console.debug('[AppUpdate]: onControllerChange=', e)
      this.updateVersion(APP_VERSION)
      // prompt the user
      this.reloadPrompt = true
    },

    // ...refresh the page to use it
    reloadApp() {
      // each client instance reloads itself
      if (!this.isReloading) {
        console.debug('[AppUpdate]: Reloading app after user agreed.')
        this.reloadPrompt = false
        window.location.reload()
        this.isReloading = true
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.c-snack {
  :deep(.v-snack__wrapper) {
    padding-right: 8px;

    .c-snack__content {
      font-size: 1.5rem;
      font-weight: bold;
    }

    .v-snack__action > .v-btn {
      padding: 16px;
    }
  }
}
</style>
